import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FilmIcon, MusicIcon, UsersIcon, LeafIcon, Gamepad2Icon, HomeIcon, CheckIcon, TreePineIcon, MicIcon, ShieldCheckIcon, WifiIcon } from 'lucide-react';
type Category = 'All' | 'Entertainment' | 'Lifestyle' | 'Infrastructure';
const experiences = [{
  id: 'cinema',
  category: 'Entertainment',
  title: 'Cinema',
  icon: FilmIcon,
  color: 'bg-[#6366F1]',
  textColor: 'text-[#6366F1]',
  ringColor: 'ring-[#6366F1]',
  shadowColor: 'shadow-[0_0_30px_rgba(99,102,241,0.2)]',
  desc: 'Private theatres engineered for precision, depth, and immersion.',
  recommendation: {
    tier: 'Signature Tier',
    items: ['Dolby Atmos 11.2.4 Reference Audio', 'MicroLED 146" Display', 'Acoustic Treatment', 'Motorized Seating Integration']
  }
}, {
  id: 'music',
  category: 'Entertainment',
  title: 'Music',
  icon: MusicIcon,
  color: 'bg-[#EC4899]',
  textColor: 'text-[#EC4899]',
  ringColor: 'ring-[#EC4899]',
  shadowColor: 'shadow-[0_0_30px_rgba(236,72,153,0.2)]',
  desc: 'Whole-home audio that flows without interruption.',
  recommendation: {
    tier: 'Premium Tier',
    items: ['Invisible Architectural Speakers', 'High-Res Audio Distribution', 'Dedicated Listening Room Calibration']
  }
}, {
  id: 'performance',
  category: 'Entertainment',
  title: 'Performance',
  icon: MicIcon,
  color: 'bg-[#F97316]',
  textColor: 'text-[#F97316]',
  ringColor: 'ring-[#F97316]',
  shadowColor: 'shadow-[0_0_30px_rgba(249,115,22,0.2)]',
  desc: 'Not a room. A stage. Mic integration, lighting scenes, screen sync.',
  recommendation: {
    tier: 'Signature Tier',
    items: ['Professional Mic Integration', 'Stage Lighting Scenes', 'Lyrics & Screen Sync', 'DJ & Live Performance Mode']
  }
}, {
  id: 'gaming',
  category: 'Entertainment',
  title: 'Gaming',
  icon: Gamepad2Icon,
  color: 'bg-[#8B5CF6]',
  textColor: 'text-[#8B5CF6]',
  ringColor: 'ring-[#8B5CF6]',
  shadowColor: 'shadow-[0_0_30px_rgba(139,92,246,0.2)]',
  desc: 'Purpose-built sanctuaries for immersive play.',
  recommendation: {
    tier: 'Premium Tier',
    items: ['Low-Latency 4K Display', 'Immersive Surround Sound', 'RGB Ambient Lighting Sync', 'Ergonomic Environment Control']
  }
}, {
  id: 'social',
  category: 'Lifestyle',
  title: 'Party & Social',
  icon: UsersIcon,
  color: 'bg-[#F59E0B]',
  textColor: 'text-[#F59E0B]',
  ringColor: 'ring-[#F59E0B]',
  shadowColor: 'shadow-[0_0_30px_rgba(245,158,11,0.2)]',
  desc: 'Karaoke rooms, lounges, and party spaces designed for energy.',
  recommendation: {
    tier: 'Premium Tier',
    items: ['Multi-Zone Party Audio', 'Dynamic Mood Lighting', 'DJ Input & Streaming Integration', 'One-Touch Hosting Scenes']
  }
}, {
  id: 'outdoor',
  category: 'Lifestyle',
  title: 'Outdoor',
  icon: TreePineIcon,
  color: 'bg-[#22D3EE]',
  textColor: 'text-[#22D3EE]',
  ringColor: 'ring-[#22D3EE]',
  shadowColor: 'shadow-[0_0_30px_rgba(34,211,238,0.2)]',
  desc: 'Gardens and terraces that transform into entertainment zones.',
  recommendation: {
    tier: 'Premium Tier',
    items: ['Landscape Audio Zones', 'Poolside Music Sync', 'Outdoor Cinema Projection', 'Garden Lighting Scenes']
  }
}, {
  id: 'relax',
  category: 'Lifestyle',
  title: 'Relaxation',
  icon: LeafIcon,
  color: 'bg-[#10B981]',
  textColor: 'text-[#10B981]',
  ringColor: 'ring-[#10B981]',
  shadowColor: 'shadow-[0_0_30px_rgba(16,185,129,0.2)]',
  desc: 'Spaces that adapt — lighting, climate, and control in harmony.',
  recommendation: {
    tier: 'Essential Tier',
    items: ['Circadian Rhythm Lighting', 'Spa Audio Zones', 'Automated Climate & Shades']
  }
}, {
  id: 'wholehome',
  category: 'Lifestyle',
  title: 'Whole Home',
  icon: HomeIcon,
  color: 'bg-[#C9A96E]',
  textColor: 'text-[#C9A96E]',
  ringColor: 'ring-[#C9A96E]',
  shadowColor: 'shadow-[0_0_30px_rgba(201,169,110,0.2)]',
  desc: 'Every room unified into one seamless living experience.',
  recommendation: {
    tier: 'Bespoke Tier',
    items: ['Whole-Home Audio Distribution', 'Centralized Automation Hub', 'Unified Control Interface', 'AI-Driven Scene Intelligence']
  }
}, {
  id: 'security',
  category: 'Infrastructure',
  title: 'Security',
  icon: ShieldCheckIcon,
  color: 'bg-[#64748B]',
  textColor: 'text-[#64748B]',
  ringColor: 'ring-[#64748B]',
  shadowColor: 'shadow-[0_0_30px_rgba(100,116,139,0.2)]',
  desc: 'Security, monitoring, and access — powerful, but unseen.',
  recommendation: {
    tier: 'Premium Tier',
    items: ['Invisible Monitoring System', 'Smart Access Control', 'Away Mode Simulation', 'Arrival & Alert Automation']
  }
}, {
  id: 'network',
  category: 'Infrastructure',
  title: 'Connectivity',
  icon: WifiIcon,
  color: 'bg-[#3B82F6]',
  textColor: 'text-[#3B82F6]',
  ringColor: 'ring-[#3B82F6]',
  shadowColor: 'shadow-[0_0_30px_rgba(59,130,246,0.2)]',
  desc: 'Enterprise-grade networking. The invisible backbone of everything.',
  recommendation: {
    tier: 'Essential Tier',
    items: ['Enterprise-Grade WiFi Mesh', 'Zero-Latency Coverage', 'Dedicated AV Network', 'Remote Management']
  }
}];
export function ExperienceBuilder() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Category>('All');
  const filteredExperiences = activeTab === 'All' ? experiences : experiences.filter(exp => exp.category === activeTab);
  return <section id="experience" className="py-32 bg-obsidian relative" data-id="element-287">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-288">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-12" data-id="element-289">
          <h2 className="text-headline font-serif text-platinum mb-4" data-id="element-290">
            We design how your home feels.
          </h2>
          <p className="text-silver max-w-2xl mx-auto" data-id="element-291">
            Select an experience. Our intelligence will design the architecture
            required to deliver it.
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-16" data-id="element-292">
          {(['All', 'Entertainment', 'Lifestyle', 'Infrastructure'] as Category[]).map(tab => <button key={tab} onClick={() => {
          setActiveTab(tab);
          setSelectedId(null);
        }} className={`relative px-6 py-2 rounded-full text-sm tracking-wide transition-colors ${activeTab === tab ? 'text-white' : 'text-silver hover:text-platinum'}`} data-id="element-293">
              {activeTab === tab && <motion.div layoutId="activeTab" className="absolute inset-0 bg-carbon border border-graphite rounded-full -z-10" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-294" />}
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-cinema via-music to-social rounded-full" data-id="element-295" />}
            </button>)}
        </div>

        <motion.div layout className={`grid gap-6 ${activeTab === 'All' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1 md:grid-cols-2'}`} data-id="element-296">
          <AnimatePresence mode="popLayout" data-id="element-297">
            {filteredExperiences.map((exp, idx) => {
            const isSelected = selectedId === exp.id;
            const Icon = exp.icon;
            return <motion.div layout key={exp.id} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} transition={{
              duration: 0.4
            }} onClick={() => setSelectedId(isSelected ? null : exp.id)} className={`cursor-pointer overflow-hidden transition-all duration-500 bg-carbon border border-graphite relative group ${isSelected ? `ring-1 ${exp.ringColor} ${exp.shadowColor} border-transparent` : 'hover:border-silver/30'}`} data-id="element-298">
                  {/* Top color accent line */}
                  <div className={`absolute top-0 left-0 right-0 h-1.5 ${exp.color} opacity-90`} data-id="element-299" />

                  <div className="p-6 md:p-8" data-id="element-300">
                    <div className="flex items-start justify-between mb-6" data-id="element-301">
                      <div className={`p-4 rounded-full bg-void border border-graphite group-hover:border-opacity-50 transition-colors`} data-id="element-302">
                        <Icon className={`w-6 h-6 ${exp.textColor}`} data-id="element-303" />
                      </div>
                      <span className="text-silver text-xs tracking-widest uppercase font-serif" data-id="element-304">
                        {exp.title}
                      </span>
                    </div>

                    <h3 className="text-lg font-serif text-platinum mb-3" data-id="element-305">
                      {exp.desc}
                    </h3>

                    <AnimatePresence data-id="element-306">
                      {isSelected && <motion.div initial={{
                    opacity: 0,
                    height: 0,
                    mt: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto',
                    mt: 24
                  }} exit={{
                    opacity: 0,
                    height: 0,
                    mt: 0
                  }} className="border-t border-graphite pt-6" data-id="element-307">
                          <p className={`${exp.textColor} text-xs tracking-widest uppercase mb-4`} data-id="element-308">
                            AI Recommendation • {exp.recommendation.tier}
                          </p>
                          <ul className="space-y-3" data-id="element-309">
                            {exp.recommendation.items.map((item, i) => <li key={i} className="flex items-start text-silver" data-id="element-310">
                                <CheckIcon className={`w-4 h-4 ${exp.textColor} mr-3 mt-0.5 shrink-0`} data-id="element-311" />
                                <span className="text-sm" data-id="element-312">{item}</span>
                              </li>)}
                          </ul>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>;
          })}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>;
}